import React from 'react';
import Header from './HeaderHome';
import Footer from './FooterHome';

const EnvironmentConservation = () => {
  // ── Key Programmes data ──
  const programmes = [
    {
      icon: "🌳",
      title: "Tree Plantation Drives",
      desc: "Organising large-scale tree plantation campaigns in urban and rural areas to increase green cover, combat climate change, and restore ecological balance.",
    },
    {
      icon: "♻️",
      title: "Waste Management & Recycling",
      desc: "Promoting waste segregation, composting, and recycling initiatives to reduce landfill waste and encourage a circular economy in communities.",
    },
    {
      icon: "💧",
      title: "Water Conservation",
      desc: "Implementing rainwater harvesting systems, rejuvenating water bodies, and promoting water-efficient practices to address water scarcity.",
    },
    {
      icon: "🌱",
      title: "Sustainable Agriculture",
      desc: "Educating farmers on organic farming, natural pest control, crop rotation, and sustainable practices that protect soil health and biodiversity.",
    },
    {
      icon: "🐝",
      title: "Biodiversity Protection",
      desc: "Protecting local flora and fauna through habitat restoration, wildlife awareness, and conservation of native species and ecosystems.",
    },
    {
      icon: "📢",
      title: "Environmental Awareness",
      desc: "Conducting workshops, school programmes, and community outreach to educate people about climate change, pollution, and sustainable living.",
    },
  ];

  // ── Impact Stats ──
  const stats = [
    { number: "25,000+", label: "Trees Planted" },
    { number: "50+", label: "Awareness Camps" },
    { number: "120+", label: "Water Bodies Revived" },
    { number: "800+", label: "Farmers Trained" },
  ];

  // ── How You Can Help ──
  const helpOptions = [
    { icon: "🌳", title: "Plant a Tree", desc: "Sponsor tree plantations or join our planting drives." },
    { icon: "♻️", title: "Reduce Waste", desc: "Support our recycling and composting initiatives." },
    { icon: "💧", title: "Save Water", desc: "Contribute to rainwater harvesting and water conservation projects." },
  ];

  return (
    <>
      <Header />

      {/* ========== HERO ========== */}
      <section className="environment-hero relative text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-block bg-primary/80 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium mb-4">
              <i className="fas fa-leaf mr-2"></i> Our Service
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">
              Environment &amp; Nature Conservation
            </h1>
            <p className="text-xl md:text-2xl text-yellow-100/90 font-light">
              Protecting our planet, preserving biodiversity, and building a sustainable future for generations
            </p>
            <div className="w-24 h-1 bg-yellow-300 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* ========== OVERVIEW ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                Protecting Our Planet, Preserving Our Future
              </h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Our planet is facing unprecedented environmental challenges—deforestation, climate change, water
              scarcity, pollution, and loss of biodiversity. At Venkatadri Seva Trust, our <strong>Environment
                &amp; Nature Conservation</strong> programme is committed to protecting and restoring the natural
              world for current and future generations.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              We believe that humanity's survival and well-being depend on a healthy planet. Through tree
              plantation drives, water conservation projects, waste management initiatives, and environmental
              education, we work to create a sustainable and harmonious relationship between people and nature.
            </p>
          </div>
        </div>
      </section>

      {/* ========== VISION & MISSION ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="vision-card rounded-2xl p-8 text-white text-center">
              <div className="text-5xl mb-4">👁️</div>
              <h3 className="text-2xl font-bold mb-3">Our Vision</h3>
              <p className="text-yellow-50/90 text-lg leading-relaxed">
                A green, clean, and sustainable world where human progress is balanced with environmental
                stewardship—ensuring a healthy planet for all living beings.
              </p>
            </div>
            <div className="vision-card rounded-2xl p-8 text-white text-center">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold mb-3">Our Mission</h3>
              <p className="text-yellow-50/90 text-lg leading-relaxed">
                To restore ecological balance through reforestation, water conservation, waste reduction, and
                community education—empowering people to become guardians of their environment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== KEY PROGRAMMES ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Key Programmes</h2>
            <p className="text-yellow-100 mt-2 text-lg">
              Taking action today for a sustainable tomorrow
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {programmes.map((programme, idx) => (
              <div
                key={idx}
                className="program-card bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-smooth text-center border-t-4 border-primary"
              >
                <div className="service-icon"><span>{programme.icon}</span></div>
                <h3 className="text-xl font-semibold text-gray-800">{programme.title}</h3>
                <p className="text-gray-600 mt-2 text-sm">{programme.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== IMPACT / STATS ========== */}
      {/* <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Impact</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mt-3 rounded-full"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center text-white">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <div className="text-4xl md:text-5xl font-bold text-yellow-300">{stat.number}</div>
                <p className="text-yellow-100/80 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ========== HOW YOU CAN HELP ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">How You Can Help</h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
              <p className="text-gray-600 mt-4 text-lg">
                Every small action counts—together we can heal our planet.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {helpOptions.map((option, idx) => (
                <div key={idx} className="text-center p-4 rounded-lg bg-gray-50 hover:bg-yellow-50 transition-smooth">
                  <div className="text-4xl text-primary mb-3">{option.icon}</div>
                  <h4 className="font-bold text-gray-800">{option.title}</h4>
                  <p className="text-gray-600 text-sm mt-1">{option.desc}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <a
                href="/donate"
                className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-dark transition-smooth inline-block"
              >
                <i className="fas fa-hand-holding-heart mr-2"></i> Support Conservation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIAL ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <div className="text-6xl mb-4">“</div>
            <p className="text-xl md:text-2xl font-light leading-relaxed italic">
              Our village faced severe water shortages every summer. Venkatadri Seva Trust helped us build
              rainwater harvesting systems and rejuvenate our pond. Now we have water year-round, and the
              surrounding greenery has returned. They taught us to care for our own environment.
            </p>
            <div className="mt-6">
              <p className="font-semibold text-yellow-200">— Ramappa, 56</p>
              <p className="text-yellow-100/70 text-sm">Rural Bengaluru</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== JOIN OUR MISSION ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Join Our Mission</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-yellow-100/90 italic mb-6">
              "The Earth does not belong to us; we belong to the Earth."
            </p>
            <p className="text-lg text-white/80 leading-relaxed mb-8">
              Your generous support—through donations, volunteering, or partnerships—helps us plant more trees,
              conserve water, reduce waste, and educate communities about sustainable living. Together, we can
              protect our planet for future generations.
            </p>
            <p className="text-xl text-yellow-100 font-light mb-8">
              Together, let us be the custodians of nature and create a greener, cleaner, and more sustainable world.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/donate"
                className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-smooth"
              >
                <i className="fas fa-heart mr-2"></i> Donate Now
              </a>
              <a
                href="/volunteer"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-smooth"
              >
                <i className="fas fa-hand-peace mr-2"></i> Volunteer
              </a>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default EnvironmentConservation;